import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Nav,
  Row,
  Spinner,
  Table,
  Tab,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../../layout/Layout";

import {
  getQualityFolderTree,
  getQualityDocumentDetail,
  updateQualityDocument,
  moveQualityDocument,
  uploadQualityDocumentVersion,
  changeQualityDocumentStatus,
  trackQualityDocumentAccess,
} from "../../../../api/kalite/qmad/qmadApi";

const DOCUMENT_TYPES = [
  { value: "FORM", label: "Form" },
  { value: "PROCEDURE", label: "Prosedür" },
  { value: "INSTRUCTION", label: "Talimat" },
  { value: "STANDARD", label: "Standart" },
  { value: "RECORD", label: "Kayıt" },
  { value: "REPORT", label: "Rapor" },
  { value: "CERTIFICATE", label: "Sertifika" },
  { value: "POLICY", label: "Politika" },
  { value: "OTHER", label: "Diğer" },
];

const STATUS_BADGE = {
  DRAFT: "secondary",
  IN_REVIEW: "warning",
  PUBLISHED: "success",
  ARCHIVED: "dark",
  PASSIVE: "danger",
};

const getPayload = (response) => response?.data || response || {};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("tr-TR");
};

const formatFileSize = (size) => {
  const value = Number(size || 0);

  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;

  return `${(value / 1024 / 1024).toFixed(1)} MB`;
};

const resolveFileUrl = (fileUrl) => {
  if (!fileUrl) return "";

  if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
    return fileUrl;
  }

  return fileUrl;
};

function QualityDocumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tokenReady, setTokenReady] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  const [loading, setLoading] = useState(false);
  const [document, setDocument] = useState(null);
  const [logs, setLogs] = useState([]);
  const [folders, setFolders] = useState([]);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [editModal, setEditModal] = useState({
    show: false,
    documentCode: "",
    documentName: "",
    documentType: "OTHER",
    department: "Kalite",
    ownerDepartment: "Kalite",
    description: "",
  });

  const [moveModal, setMoveModal] = useState({
    show: false,
    folderId: "",
  });

  const [versionModal, setVersionModal] = useState({
    show: false,
    revisionNo: "",
    revisionDate: "",
    changeNote: "",
    publish: false,
    file: null,
  });

  const [processModal, setProcessModal] = useState({
    show: false,
    processKey: "",
    processName: "",
    moduleKey: "",
    modulePath: "",
    relationType: "REFERENCE",
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const token = sessionStorage.getItem("token");
      if (token) {
        setTokenReady(true);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const folderOptions = useMemo(() => {
    return folders.map((folder) => ({
      value: folder._id,
      label: folder.path || folder.name,
    }));
  }, [folders]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const loadDetail = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();

      const response = await getQualityDocumentDetail({
        documentId: id,
      });

      const payload = getPayload(response);

      setDocument(payload.document || null);
      setLogs(payload.logs || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Doküman detayı alınamadı.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadFolders = useCallback(async () => {
    try {
      const response = await getQualityFolderTree({
        department: "Kalite",
        includePassive: false,
      });

      const payload = getPayload(response);
      setFolders(payload.folders || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Klasörler alınamadı.");
    }
  }, []);

  useEffect(() => {
    if (!tokenReady) return;

    loadDetail();
    loadFolders();
  }, [tokenReady, loadDetail, loadFolders]);

  const openEditModal = () => {
    if (!document) return;

    clearMessages();

    setEditModal({
      show: true,
      documentCode: document.documentCode || "",
      documentName: document.documentName || "",
      documentType: document.documentType || "OTHER",
      department: document.department || "Kalite",
      ownerDepartment: document.ownerDepartment || "Kalite",
      description: document.description || "",
    });
  };

  const closeEditModal = () => {
    setEditModal((prev) => ({ ...prev, show: false }));
  };

  const submitEdit = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      clearMessages();

      await updateQualityDocument({
        documentId: id,
        documentCode: editModal.documentCode,
        documentName: editModal.documentName,
        documentType: editModal.documentType,
        department: editModal.department,
        ownerDepartment: editModal.ownerDepartment,
        description: editModal.description,
      });

      setSuccess("Doküman güncellendi.");
      closeEditModal();
      await loadDetail();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Doküman güncellenemedi.");
    } finally {
      setLoading(false);
    }
  };

  const openMoveModal = () => {
    setMoveModal({
      show: true,
      folderId: document?.folderId?._id || document?.folderId || "",
    });
  };

  const closeMoveModal = () => {
    setMoveModal((prev) => ({ ...prev, show: false }));
  };

  const submitMove = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      clearMessages();

      await moveQualityDocument({
        documentId: id,
        folderId: moveModal.folderId,
      });

      setSuccess("Doküman taşındı.");
      closeMoveModal();
      await loadDetail();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Doküman taşınamadı.");
    } finally {
      setLoading(false);
    }
  };

  const openVersionModal = () => {
    clearMessages();

    const nextRevisionNo = Number(document?.currentRevisionNo || 0) + 1;

    setVersionModal({
      show: true,
      revisionNo: nextRevisionNo,
      revisionDate: "",
      changeNote: "",
      publish: false,
      file: null,
    });
  };

  const closeVersionModal = () => {
    setVersionModal((prev) => ({ ...prev, show: false }));
  };

  const submitVersion = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      clearMessages();

      if (!versionModal.file) {
        setError("Yeni revizyon için dosya seçmelisin.");
        return;
      }

      const formData = new FormData();

      formData.append("documentId", id);
      formData.append("revisionNo", versionModal.revisionNo);
      formData.append("revisionDate", versionModal.revisionDate);
      formData.append("changeNote", versionModal.changeNote);
      formData.append("publish", versionModal.publish);
      formData.append("file", versionModal.file);

      await uploadQualityDocumentVersion(formData);

      setSuccess("Yeni revizyon yüklendi.");
      closeVersionModal();
      await loadDetail();
      setActiveTab("versions");
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Revizyon yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    const confirmed = window.confirm(`Doküman durumu "${status}" yapılacak. Emin misin?`);

    if (!confirmed) return;

    try {
      setLoading(true);
      clearMessages();

      await changeQualityDocumentStatus({
        documentId: id,
        status,
      });

      setSuccess("Doküman durumu güncellendi.");
      await loadDetail();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Durum güncellenemedi.");
    } finally {
      setLoading(false);
    }
  };

  const handleAccess = async (accessType = "VIEW") => {
    try {
      clearMessages();

      const response = await trackQualityDocumentAccess({
        documentId: id,
        accessType,
      });

      const payload = getPayload(response);
      const fileUrl = resolveFileUrl(payload.fileUrl || document?.currentFileUrl);

      if (!fileUrl) {
        setError("Dosya bağlantısı bulunamadı.");
        return;
      }

      window.open(fileUrl, "_blank", "noopener,noreferrer");
      await loadDetail();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Dosya açılamadı.");
    }
  };

  const openProcessModal = () => {
    clearMessages();

    setProcessModal({
      show: true,
      processKey: "",
      processName: "",
      moduleKey: "",
      modulePath: "",
      relationType: "REFERENCE",
    });
  };

  const closeProcessModal = () => {
    setProcessModal((prev) => ({ ...prev, show: false }));
  };

  const submitProcess = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      clearMessages();

      const currentProcesses = document?.relatedProcesses || [];

      const newProcesses = [
        ...currentProcesses,
        {
          processKey: processModal.processKey,
          processName: processModal.processName,
          moduleKey: processModal.moduleKey,
          modulePath: processModal.modulePath,
          relationType: processModal.relationType,
        },
      ];

      await updateQualityDocument({
        documentId: id,
        relatedProcesses: JSON.stringify(newProcesses),
      });

      setSuccess("Bağlı süreç eklendi.");
      closeProcessModal();
      await loadDetail();
      setActiveTab("processes");
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Bağlı süreç eklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  const removeProcess = async (index) => {
    const confirmed = window.confirm("Bu bağlı süreç kaldırılacak. Emin misin?");
    if (!confirmed) return;

    try {
      setLoading(true);
      clearMessages();

      const newProcesses = [...(document?.relatedProcesses || [])];
      newProcesses.splice(index, 1);

      await updateQualityDocument({
        documentId: id,
        relatedProcesses: JSON.stringify(newProcesses),
      });

      setSuccess("Bağlı süreç kaldırıldı.");
      await loadDetail();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Bağlı süreç kaldırılamadı.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !document) {
    return (
      <Layout>
        <div className="container-fluid py-5 text-center">
          <Spinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="mb-2"
              onClick={() => navigate("/QualityFileManagerPage")}
            >
              ← Dosya Yönetimine Dön
            </Button>

            <h4 className="mb-1">{document?.documentName || "Doküman Detayı"}</h4>

            <div className="text-muted">
              {document?.documentCode || "-"} · Rev.{document?.currentRevisionNo || 0}
              {" · "}
              <Badge bg={STATUS_BADGE[document?.status] || "secondary"}>
                {document?.status || "-"}
              </Badge>
            </div>
          </div>

          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={() => handleAccess("VIEW")}>
              Görüntüle
            </Button>
            <Button variant="outline-success" onClick={() => handleAccess("DOWNLOAD")}>
              İndir
            </Button>
            <Button variant="primary" onClick={openVersionModal}>
              + Revizyon
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess("")} dismissible>
            {success}
          </Alert>
        )}

        <Card>
          <Card.Body>
            <Tab.Container activeKey={activeTab} onSelect={(key) => setActiveTab(key)}>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="general">Genel Bilgiler</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="versions">Versiyonlar</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="permissions">Yetkiler</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="processes">Bağlı Süreçler</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="logs">İşlem Geçmişi</Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content>
                <Tab.Pane eventKey="general">
                  <Row>
                    <Col lg={8}>
                      <Table bordered size="sm">
                        <tbody>
                          <tr>
                            <th style={{ width: 220 }}>Doküman Kodu</th>
                            <td>{document?.documentCode || "-"}</td>
                          </tr>
                          <tr>
                            <th>Doküman Adı</th>
                            <td>{document?.documentName || "-"}</td>
                          </tr>
                          <tr>
                            <th>Doküman Tipi</th>
                            <td>{document?.documentType || "-"}</td>
                          </tr>
                          <tr>
                            <th>Klasör</th>
                            <td>{document?.folderId?.path || document?.folderId?.name || "-"}</td>
                          </tr>
                          <tr>
                            <th>Departman</th>
                            <td>{document?.department || "-"}</td>
                          </tr>
                          <tr>
                            <th>Sorumlu Departman</th>
                            <td>{document?.ownerDepartment || "-"}</td>
                          </tr>
                          <tr>
                            <th>Durum</th>
                            <td>
                              <Badge bg={STATUS_BADGE[document?.status] || "secondary"}>
                                {document?.status || "-"}
                              </Badge>
                            </td>
                          </tr>
                          <tr>
                            <th>Revizyon</th>
                            <td>Rev.{document?.currentRevisionNo || 0}</td>
                          </tr>
                          <tr>
                            <th>Revizyon Tarihi</th>
                            <td>{formatDate(document?.currentRevisionDate)}</td>
                          </tr>
                          <tr>
                            <th>Güncel Dosya</th>
                            <td>
                              {document?.currentFileName || "-"}{" "}
                              <span className="text-muted">
                                ({formatFileSize(document?.currentFileSize)})
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <th>Açıklama</th>
                            <td>{document?.description || "-"}</td>
                          </tr>
                          <tr>
                            <th>Oluşturma</th>
                            <td>{formatDate(document?.createdAt)}</td>
                          </tr>
                          <tr>
                            <th>Güncelleme</th>
                            <td>{formatDate(document?.updatedAt)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>

                    <Col lg={4}>
                      <Card className="mb-3">
                        <Card.Header>
                          <strong>İşlemler</strong>
                        </Card.Header>
                        <Card.Body className="d-grid gap-2">
                          <Button variant="outline-primary" onClick={openEditModal}>
                            Bilgileri Düzenle
                          </Button>
                          <Button variant="outline-secondary" onClick={openMoveModal}>
                            Klasöre Taşı
                          </Button>
                          <Button variant="outline-success" onClick={() => handleStatusChange("PUBLISHED")}>
                            Yayına Al
                          </Button>
                          <Button variant="outline-warning" onClick={() => handleStatusChange("IN_REVIEW")}>
                            İncelemeye Al
                          </Button>
                          <Button variant="outline-dark" onClick={() => handleStatusChange("ARCHIVED")}>
                            Arşive Al
                          </Button>
                        </Card.Body>
                      </Card>

                      <Card>
                        <Card.Header>
                          <strong>Kullanım</strong>
                        </Card.Header>
                        <Card.Body>
                          <div>İndirme: {document?.downloadCount || 0}</div>
                          <div>Son görüntüleme: {formatDate(document?.lastViewedAt)}</div>
                          <div>Son indirme: {formatDate(document?.lastDownloadedAt)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab.Pane>

                <Tab.Pane eventKey="versions">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Versiyon / Revizyon Geçmişi</h6>
                    <Button size="sm" onClick={openVersionModal}>
                      + Yeni Revizyon
                    </Button>
                  </div>

                  <div className="table-responsive">
                    <Table bordered hover size="sm" className="align-middle">
                      <thead>
                        <tr>
                          <th>Revizyon</th>
                          <th>Revizyon Tarihi</th>
                          <th>Dosya</th>
                          <th>Boyut</th>
                          <th>Yükleyen</th>
                          <th>Yükleme Tarihi</th>
                          <th>Yayın Tarihi</th>
                          <th>Not</th>
                          <th>Güncel</th>
                        </tr>
                      </thead>
                      <tbody>
                        {document?.versions?.length > 0 ? (
                          document.versions.map((version) => (
                            <tr key={version._id}>
                              <td>Rev.{version.revisionNo}</td>
                              <td>{formatDate(version.revisionDate)}</td>
                              <td>{version.originalFileName || version.fileName}</td>
                              <td>{formatFileSize(version.fileSize)}</td>
                              <td>{version.uploadedByName || "-"}</td>
                              <td>{formatDate(version.uploadedAt)}</td>
                              <td>{formatDate(version.publishedAt)}</td>
                              <td>{version.changeNote || "-"}</td>
                              <td>
                                {version.isCurrent ? (
                                  <Badge bg="success">Güncel</Badge>
                                ) : (
                                  <Badge bg="secondary">Eski</Badge>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={9} className="text-center text-muted">
                              Versiyon yok.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Tab.Pane>

                <Tab.Pane eventKey="permissions">
                  <Alert variant="light">
                    MVP amaclı kullanıma kapalı. Görüntü için kullanılıyor.
                  </Alert>

                  <Row>
                    <Col md={6}>
                      <Card className="mb-3">
                        <Card.Header>Departman Yetkileri</Card.Header>
                        <Card.Body>
                          <div>
                            <strong>Görüntüleyebilen Departmanlar:</strong>
                          </div>
                          <div className="text-muted">
                            {document?.viewDepartments?.length
                              ? document.viewDepartments.join(", ")
                              : "-"}
                          </div>

                          <hr />

                          <div>
                            <strong>Düzenleyebilen Departmanlar:</strong>
                          </div>
                          <div className="text-muted">
                            {document?.editDepartments?.length
                              ? document.editDepartments.join(", ")
                              : "-"}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={6}>
                      <Card>
                        <Card.Header>Rol Yetkileri</Card.Header>
                        <Card.Body>
                          <div>
                            <strong>Görüntüleme Rolleri:</strong> {document?.viewRoles?.length || 0}
                          </div>
                          <div>
                            <strong>Düzenleme Rolleri:</strong> {document?.editRoles?.length || 0}
                          </div>
                          <div>
                            <strong>Onay Rolleri:</strong> {document?.approveRoles?.length || 0}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab.Pane>

                <Tab.Pane eventKey="processes">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Bağlı Süreçler</h6>
                    <Button size="sm" onClick={openProcessModal}>
                      + Süreç Ekle
                    </Button>
                  </div>

                  <Table bordered hover size="sm" className="align-middle">
                    <thead>
                      <tr>
                        <th>Süreç Key</th>
                        <th>Süreç Adı</th>
                        <th>Modül</th>
                        <th>Modül Yolu</th>
                        <th>İlişki Tipi</th>
                        <th style={{ width: 80 }}>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {document?.relatedProcesses?.length > 0 ? (
                        document.relatedProcesses.map((process, index) => (
                          <tr key={`${process.processKey}-${index}`}>
                            <td>{process.processKey || "-"}</td>
                            <td>{process.processName || "-"}</td>
                            <td>{process.moduleKey || "-"}</td>
                            <td>{process.modulePath || "-"}</td>
                            <td>{process.relationType || "REFERENCE"}</td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => removeProcess(index)}
                              >
                                Sil
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center text-muted">
                            Bağlı süreç yok.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Tab.Pane>

                <Tab.Pane eventKey="logs">
                  <h6 className="mb-3">İşlem Geçmişi</h6>

                  <Table bordered hover size="sm" className="align-middle">
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>İşlem</th>
                        <th>Kullanıcı</th>
                        <th>Açıklama</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.length > 0 ? (
                        logs.map((log) => (
                          <tr key={log._id}>
                            <td>{formatDate(log.createdAt)}</td>
                            <td>
                              <Badge bg="secondary">{log.action}</Badge>
                            </td>
                            <td>{log.userName || "-"}</td>
                            <td>{log.description || "-"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="text-center text-muted">
                            Log kaydı yok.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Card.Body>
        </Card>

        <Modal show={editModal.show} onHide={closeEditModal} size="lg" centered>
          <Form onSubmit={submitEdit}>
            <Modal.Header closeButton>
              <Modal.Title>Doküman Bilgilerini Düzenle</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Doküman Kodu</Form.Label>
                    <Form.Control
                      value={editModal.documentCode}
                      onChange={(e) =>
                        setEditModal((prev) => ({
                          ...prev,
                          documentCode: e.target.value,
                        }))
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tip</Form.Label>
                    <Form.Select
                      value={editModal.documentType}
                      onChange={(e) =>
                        setEditModal((prev) => ({
                          ...prev,
                          documentType: e.target.value,
                        }))
                      }
                    >
                      {DOCUMENT_TYPES.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Departman</Form.Label>
                    <Form.Control
                      value={editModal.department}
                      onChange={(e) =>
                        setEditModal((prev) => ({
                          ...prev,
                          department: e.target.value,
                        }))
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Doküman Adı</Form.Label>
                <Form.Control
                  value={editModal.documentName}
                  onChange={(e) =>
                    setEditModal((prev) => ({
                      ...prev,
                      documentName: e.target.value,
                    }))
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Sorumlu Departman</Form.Label>
                <Form.Control
                  value={editModal.ownerDepartment}
                  onChange={(e) =>
                    setEditModal((prev) => ({
                      ...prev,
                      ownerDepartment: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Açıklama</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={editModal.description}
                  onChange={(e) =>
                    setEditModal((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeEditModal}>
                Vazgeç
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                Kaydet
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={moveModal.show} onHide={closeMoveModal} centered>
          <Form onSubmit={submitMove}>
            <Modal.Header closeButton>
              <Modal.Title>Dokümanı Taşı</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form.Group>
                <Form.Label>Hedef Klasör</Form.Label>
                <Form.Select
                  value={moveModal.folderId}
                  onChange={(e) =>
                    setMoveModal((prev) => ({
                      ...prev,
                      folderId: e.target.value,
                    }))
                  }
                  required
                >
                  <option value="">Klasör seç</option>
                  {folderOptions.map((folder) => (
                    <option key={folder.value} value={folder.value}>
                      {folder.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeMoveModal}>
                Vazgeç
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                Taşı
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={versionModal.show} onHide={closeVersionModal} centered>
          <Form onSubmit={submitVersion}>
            <Modal.Header closeButton>
              <Modal.Title>Yeni Revizyon Yükle</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Revizyon No</Form.Label>
                <Form.Control
                  type="number"
                  value={versionModal.revisionNo}
                  onChange={(e) =>
                    setVersionModal((prev) => ({
                      ...prev,
                      revisionNo: e.target.value,
                    }))
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Revizyon Tarihi</Form.Label>
                <Form.Control
                  type="date"
                  value={versionModal.revisionDate}
                  onChange={(e) =>
                    setVersionModal((prev) => ({
                      ...prev,
                      revisionDate: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Değişiklik Notu</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={versionModal.changeNote}
                  onChange={(e) =>
                    setVersionModal((prev) => ({
                      ...prev,
                      changeNote: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Dosya</Form.Label>
                <Form.Control
                  type="file"
                  onChange={(e) =>
                    setVersionModal((prev) => ({
                      ...prev,
                      file: e.target.files?.[0] || null,
                    }))
                  }
                  required
                />
              </Form.Group>

              <Form.Check
                type="switch"
                label="Yükledikten sonra yayına al"
                checked={versionModal.publish}
                onChange={(e) =>
                  setVersionModal((prev) => ({
                    ...prev,
                    publish: e.target.checked,
                  }))
                }
              />
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeVersionModal}>
                Vazgeç
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                Revizyon Yükle
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={processModal.show} onHide={closeProcessModal} centered>
          <Form onSubmit={submitProcess}>
            <Modal.Header closeButton>
              <Modal.Title>Bağlı Süreç Ekle</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Süreç Key</Form.Label>
                <Form.Control
                  placeholder="shipment_loading_control"
                  value={processModal.processKey}
                  onChange={(e) =>
                    setProcessModal((prev) => ({
                      ...prev,
                      processKey: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Süreç Adı</Form.Label>
                <Form.Control
                  placeholder="Sevkiyat Yükleme Kontrol"
                  value={processModal.processName}
                  onChange={(e) =>
                    setProcessModal((prev) => ({
                      ...prev,
                      processName: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Modül Key</Form.Label>
                <Form.Control
                  placeholder="quality_loading_control"
                  value={processModal.moduleKey}
                  onChange={(e) =>
                    setProcessModal((prev) => ({
                      ...prev,
                      moduleKey: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Modül Yolu</Form.Label>
                <Form.Control
                  placeholder="/quality/loading-control"
                  value={processModal.modulePath}
                  onChange={(e) =>
                    setProcessModal((prev) => ({
                      ...prev,
                      modulePath: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>İlişki Tipi</Form.Label>
                <Form.Control
                  value={processModal.relationType}
                  onChange={(e) =>
                    setProcessModal((prev) => ({
                      ...prev,
                      relationType: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeProcessModal}>
                Vazgeç
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                Ekle
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </Layout>
  );
}

export default QualityDocumentDetailPage;